import dayjs from 'dayjs';
import { Invoice, InvoiceFrequencyType, InvoiceItemType } from 'entities/Invoice';
import { roundNumber } from 'shared/utils/helpers/roundNumber';

export interface BoxRentPricePeriod {
  startPeriodNumber: number;
  endPeriodNumber: number;
  price: number;
}

interface RentInvoicePeriod {
  dateFrom: string;
  dateTo?: string;
  initialPrice: number;
  discountAmount: number;
  createdAt: string;
}

const RENT_INVOICE_ITEM_TYPES = [InvoiceItemType.BOX, InvoiceItemType.OFFICE];

const getPeriodNumber = (date: string, startDate: string, invoiceFrequencyType: InvoiceFrequencyType, contractDuration: number): number => {
  const periodNumber = dayjs(date).startOf('day').diff(dayjs(startDate).startOf('day'), invoiceFrequencyType) + 1;

  if (periodNumber < 1) {
    return 1;
  }

  if (contractDuration > 0 && periodNumber > contractDuration) {
    return contractDuration;
  }

  return periodNumber;
};

const getCoveredPeriodCount = (dateFrom: string, dateTo: string | undefined, invoiceFrequencyType: InvoiceFrequencyType): number => {
  if (!dateTo) {
    return 1;
  }

  const from = dayjs(dateFrom).startOf('day');
  const toExclusive = dayjs(dateTo).startOf('day').add(1, 'day');
  const diff = toExclusive.diff(from, invoiceFrequencyType);

  return Math.max(1, Math.round(diff));
};

const getRentInvoicePeriods = (invoices: Invoice[]): RentInvoicePeriod[] => {
  return invoices
    .filter((invoice) => !invoice.cancellationDate)
    .reduce<RentInvoicePeriod[]>((acc, invoice) => {
      const rentItem = invoice.invoiceItems?.find((item) => RENT_INVOICE_ITEM_TYPES.includes(item.invoiceItemType));
      const dateFrom = rentItem?.dateFrom || invoice.dateFrom;

      if (!rentItem || !dateFrom) {
        return acc;
      }

      acc.push({
        dateFrom,
        dateTo: rentItem.dateTo || invoice.dateTo,
        initialPrice: rentItem.initialPrice,
        discountAmount: rentItem.discountAmount || 0,
        createdAt: invoice.createdAt,
      });

      return acc;
    }, [])
    .sort((first, second) => {
      const dateDiff = dayjs(first.dateFrom).valueOf() - dayjs(second.dateFrom).valueOf();

      if (dateDiff !== 0) {
        return dateDiff;
      }

      return dayjs(first.createdAt).valueOf() - dayjs(second.createdAt).valueOf();
    });
};

export const getLastInvoicedBoxBasePrice = (invoices: Invoice[], invoiceFrequencyType: InvoiceFrequencyType): number | undefined => {
  const rentInvoices = getRentInvoicePeriods(invoices);
  const lastInvoice = rentInvoices[rentInvoices.length - 1];

  if (!lastInvoice) {
    return undefined;
  }

  const periodCount = getCoveredPeriodCount(lastInvoice.dateFrom, lastInvoice.dateTo, invoiceFrequencyType);

  return roundNumber(lastInvoice.initialPrice / periodCount);
};

const groupConsecutivePrices = (pricesByPeriod: Array<number | undefined>, contractDuration: number): BoxRentPricePeriod[] => {
  const periods: BoxRentPricePeriod[] = [];

  for (let period = 1; period <= contractDuration; period += 1) {
    const price = pricesByPeriod[period];

    if (price == null) {
      continue;
    }

    const lastPeriod = periods[periods.length - 1];

    if (lastPeriod && lastPeriod.price === price && lastPeriod.endPeriodNumber === period - 1) {
      lastPeriod.endPeriodNumber = period;
    } else {
      periods.push({
        startPeriodNumber: period,
        endPeriodNumber: period,
        price,
      });
    }
  }

  return periods;
};

export const getBoxRentPricePeriodsFromInvoices = (
  invoices: Invoice[],
  startDate: string,
  contractDuration: number,
  invoiceFrequencyType: InvoiceFrequencyType,
  currentPrice: number,
): BoxRentPricePeriod[] => {
  if (contractDuration <= 0) {
    return [];
  }

  const pricesByPeriod: Array<number | undefined> = [];
  const rentInvoices = getRentInvoicePeriods(invoices);

  rentInvoices.forEach((invoice) => {
    const startPeriod = getPeriodNumber(invoice.dateFrom, startDate, invoiceFrequencyType, contractDuration);
    const periodCount = getCoveredPeriodCount(invoice.dateFrom, invoice.dateTo, invoiceFrequencyType);
    const unitPrice = roundNumber(Math.max(0, invoice.initialPrice - invoice.discountAmount) / periodCount);

    for (let offset = 0; offset < periodCount; offset += 1) {
      const period = startPeriod + offset;

      if (period >= 1 && period <= contractDuration) {
        pricesByPeriod[period] = unitPrice;
      }
    }
  });

  const firstFilledPeriod = pricesByPeriod.findIndex((price) => price != null);
  const firstInvoicePrice = firstFilledPeriod > 0 ? pricesByPeriod[firstFilledPeriod] : currentPrice;
  let lastFilledPeriod = 0;

  for (let period = 1; period <= contractDuration; period += 1) {
    if (pricesByPeriod[period] != null) {
      lastFilledPeriod = period;
    }
  }

  for (let period = 1; period <= contractDuration; period += 1) {
    if (pricesByPeriod[period] != null) {
      continue;
    }

    if (period > lastFilledPeriod) {
      pricesByPeriod[period] = currentPrice;
      continue;
    }

    pricesByPeriod[period] = period < firstFilledPeriod ? firstInvoicePrice : pricesByPeriod[period - 1];
  }

  return groupConsecutivePrices(pricesByPeriod, contractDuration);
};

export const getBoxRentPricePeriodsFromDiscounts = (
  contractPeriodsUnitPrices: Array<{ periodNumber: number; rate: number }>,
): BoxRentPricePeriod[] => {
  const groupedPrices = contractPeriodsUnitPrices.reduce((acc: Record<number, number[]>, priceObject) => {
    if (acc[priceObject.rate]) {
      acc[priceObject.rate].push(priceObject.periodNumber);
    } else {
      acc[priceObject.rate] = [priceObject.periodNumber];
    }

    return acc;
  }, {});

  return Object.entries(groupedPrices).map(([rate, periodNumbers]) => {
    const sortedPeriodNumbers = [...periodNumbers].sort((first, second) => first - second);

    return {
      startPeriodNumber: sortedPeriodNumbers[0],
      endPeriodNumber: sortedPeriodNumbers[sortedPeriodNumbers.length - 1],
      price: Number(rate),
    };
  });
};
